import { FormLayout, Modal, TextField, Form, Select } from '@shopify/polaris';
import { useCallback, useEffect, useMemo, useState } from 'react';

import helpers from 'helpers';

import { lengthLessThan, notEmpty, useField, useForm } from '@shopify/react-form';

import __ from 'languages/index';
import { Vehicle, VehicleStatus, useCreateVehicle, useGetVehicle, useUpdateVehicle } from 'queries/charge_station.query';

export default function VehicleModal({ onClose, show, currentVehicleId }: { onClose: Function; show: boolean; currentVehicleId?: number | null }) {
  const { mutateAsync: createEntity, isPending: creating, isSuccess: creatingSuccess } = useCreateVehicle();
  const { mutateAsync: updateEntity, isPending: updating, isSuccess: updateSuccess } = useUpdateVehicle();
  const { mutateAsync: getVehicle, data: vehicleData } = useGetVehicle();

  const [entity, setEntity] = useState<Vehicle>(null);

  const fetchEntity = useCallback(async (currentVehicleId) => {
    await getVehicle(currentVehicleId);
  }, []);

  useEffect(() => {
    if (currentVehicleId) {
      fetchEntity(currentVehicleId);
    } else {
      setEntity(null);
    }
  }, [currentVehicleId, fetchEntity]);

  const statusOptions = useMemo(
    () => [
      { label: 'Chưa duyệt', value: VehicleStatus.UNAPPROVED },
      { label: 'Đã duyệt', value: VehicleStatus.APPROVED },
      { label: 'Whitelist', value: VehicleStatus.WHITELIST },
      { label: 'Bị cấm', value: VehicleStatus.BANNED },
    ],
    []
  );

  const useFields = {
    tagId: useField<string>({
      value: entity?.tagId ?? '',
      validates: [notEmpty('Không được để trống trường này.'), lengthLessThan(100, 'No more than 100 characters.')],
    }),

    licensePlate: useField<string>({
      value: entity?.licensePlate ?? '',
      validates: [lengthLessThan(30, 'No more than 30 characters.')],
    }),

    brand: useField<string>({
      value: entity?.brand ?? '',
      validates: [lengthLessThan(100, 'No more than 100 characters.')],
    }),

    vehicleType: useField<string>({
      value: entity?.vehicleType ?? '',
      validates: [lengthLessThan(100, 'No more than 100 characters.')],
    }),

    manufactureYear: useField<string>({
      value: entity?.manufactureYear !== null && entity?.manufactureYear !== undefined ? String(entity.manufactureYear) : '',
      validates: [
        (inputVal) => {
          if (!inputVal) return;
          if (!helpers.isNumber(inputVal)) return 'Phải là một số hợp lệ.';
        },
      ],
    }),

    status: useField<string>({
      value: entity?.status ?? VehicleStatus.UNAPPROVED,
      validates: [],
    }),
  };

  const {
    fields,
    submit,
    dirty,
    reset: resetForm,
  } = useForm({
    fields: useFields,

    async onSubmit(values) {
      try {
        const payload = {
          tagId: values.tagId,
          licensePlate: values.licensePlate || null,
          brand: values.brand || null,
          vehicleType: values.vehicleType || null,
          manufactureYear: values.manufactureYear ? helpers.parseNumeric(values.manufactureYear) : null,
          status: values.status as VehicleStatus,
        };

        if (!currentVehicleId) {
          await createEntity(payload as Vehicle);
        } else {
          await updateEntity({
            id: currentVehicleId,
            ...(payload as Vehicle),
          });
        }
      } catch (e: any) {
        if (e.params !== undefined) {
          if (e.params.field !== undefined) {
            useFields[e.params.field].setError(__('check_this_field_again'));
          }
        }
      }
      return { status: 'success' };
    },
  });

  useEffect(() => {
    if (vehicleData) {
      setEntity(vehicleData.vehicle_data);
      resetForm();
    }
  }, [vehicleData, resetForm]);

  useEffect(() => {
    if (updateSuccess || creatingSuccess) {
      resetForm();
      onClose();
    }
  }, [updateSuccess, creatingSuccess]);

  useEffect(() => {
    if (!currentVehicleId) {
      resetForm();
      setEntity(null);
    }
  }, [currentVehicleId]);

  const Actual_page = (
    <Modal
      open={show}
      onClose={() => onClose()}
      title={currentVehicleId ? 'Cập nhật xe' : 'Tạo xe'}
      primaryAction={{
        content: currentVehicleId ? 'Cập nhật' : 'Tạo',
        disabled: !dirty,
        loading: updating || creating,
        onAction: submit,
      }}
      secondaryActions={[
        {
          content: 'Close',
          onAction: () => onClose(),
        },
      ]}
    >
      <Modal.Section>
        <Form onSubmit={submit}>
          <FormLayout>
            <TextField autoFocus autoComplete="off" requiredIndicator label="Tag ID" {...fields.tagId} />
            <FormLayout.Group>
              <TextField autoComplete="off" label="Biển số" {...fields.licensePlate} />
              <TextField autoComplete="off" label="Hãng xe" {...fields.brand} />
            </FormLayout.Group>
            <FormLayout.Group>
              <TextField autoComplete="off" label="Loại xe" {...fields.vehicleType} />
              <TextField type="number" autoComplete="off" label="Năm sản xuất" {...fields.manufactureYear} />
            </FormLayout.Group>
            <Select label="Trạng thái" options={statusOptions} value={fields.status.value} onChange={(value) => fields.status.onChange(value)} />
          </FormLayout>
        </Form>
      </Modal.Section>
    </Modal>
  );

  return <>{Actual_page}</>;
}
